import { useState } from 'react'
import { Formik, type FormikHelpers } from 'formik'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'

import { useCreateReturnPortalPickupRequest, useCreateReturnPortalServicePointReturnOrder } from '@dhl/mdp-api'
import {
  createInitialReturnRequest,
  useCreateReturnRequestValidationSchema,
  type ReturnPortal,
  type ReturnRequest,
  toPickupRequestDto,
  toServicePointReturnRequestDto,
} from '~/model'
import { CompanyInfo, ReturnForm } from '~/components'
import { useHandleReturnRequestError, useTranslationContext } from '~/hooks'
import { languageToLocale, validateSchema, persistLabels } from '~/helpers'

type Props = {
  returnPortal: ReturnPortal
}

export const ReturnFormContainer = ({ returnPortal }: Props) => {
  const { t, i18n } = useTranslation()
  const initialReturnRequest = createInitialReturnRequest({
    returnPortal,
    t,
    language: i18n.language,
  })
  const navigate = useNavigate()
  const [countryCode, setCountryCode] = useState<string>(initialReturnRequest.address.countryCode.key)
  const { customerPortalId, businessUnitCode } = returnPortal
  const { handleReturnRequestError } = useHandleReturnRequestError(businessUnitCode)
  const translationContext = useTranslationContext(businessUnitCode)
  const locale = languageToLocale(i18n.language)
  const validationSchema = useCreateReturnRequestValidationSchema(translationContext, countryCode)

  const { mutateAsync: postPickup } = useCreateReturnPortalPickupRequest()
  const { mutateAsync: postServicePointReturn } = useCreateReturnPortalServicePointReturnOrder()

  const onSubmit = async (returnRequest: ReturnRequest, { setFieldValue }: FormikHelpers<ReturnRequest>) => {
    const { returnType } = returnRequest
    try {
      // Persistence for pickup and consumer_pickup:
      if (returnType === 'pickup' || returnType === 'consumer_pickup') {
        const pickupRequestDto = toPickupRequestDto(returnRequest)
        await postPickup(
          {
            customerPortalId,
            data: pickupRequestDto,
          },
          {
            onSuccess: (data) => {
              navigate(`/success/pickup/${data.confirmationNumber}?businessUnit=${businessUnitCode}&locale=${locale}`, {
                replace: true,
              })
            },
          }
        )
      }
      // Persistence for service_point return:
      if (returnType === 'service_point') {
        const servicePointReturnRequestDto = toServicePointReturnRequestDto(returnRequest)
        await postServicePointReturn(
          {
            customerPortalId,
            data: servicePointReturnRequestDto,
            params: {
              locale,
            },
          },
          {
            onSuccess: (data) => {
              const {
                servicePointLocatorUrl = '',
                servicePointIsPostalOffice = businessUnitCode === 'ccc',
                labels,
              } = data

              persistLabels(labels)

              navigate(
                `/success/service-point?businessUnit=${businessUnitCode}&countryCode=${countryCode}&labelType=${
                  servicePointReturnRequestDto.returnDeclarationMechanism || ''
                }&cccLocatorUrl=${servicePointLocatorUrl}&locale=${locale}&postalOffice=${servicePointIsPostalOffice}`
              )
            },
            onError: ({ message }) => {
              handleReturnRequestError({ data: { message } }, setFieldValue)
            },
          }
        )
      }
    } catch (error) {
      handleReturnRequestError(error, setFieldValue)
    }
  }

  const validate = async (values: ReturnRequest) => (validationSchema ? validateSchema(validationSchema, values) : {})

  return (
    <Formik<ReturnRequest>
      initialValues={initialReturnRequest}
      onSubmit={onSubmit}
      validate={validate}
      validateOnChange
    >
      <Grid container direction="column" spacing={2}>
        <Grid>
          <CompanyInfo returnPortal={returnPortal} />
        </Grid>
        <Grid>
          <ReturnForm returnPortal={returnPortal} onCountryChange={setCountryCode} />
        </Grid>
      </Grid>
    </Formik>
  )
}
